import { useEffect, useMemo, useState } from 'react';

import { logger } from '../../../../../common/utils/logger';

const DEFAULT_TTL = 5 * 60 * 1000;

interface CachedItem {
	ttl: number;
	data: any;
}

abstract class InMemCache {
	private static cache: Record<string, CachedItem> = {};

	public static setCacheData(key: string, data: any): void {
		this.cache[key] = {
			ttl: Date.now() + DEFAULT_TTL,
			data,
		};
	}

	public static getCacheData(key: string) {
		return key in this.cache && Date.now() < this.cache[key].ttl ? this.cache[key].data : undefined;
	}
}

export const useAdminPermissions: ({ cloudId, orgId }: { cloudId: string; orgId: string }) => {
	hasBookmarkCreationAccess: boolean;
	hasAdminhubAccess: boolean;
	loading: boolean;
} = ({ cloudId, orgId }: { cloudId: string; orgId: string }) => {
	const [loading, setLoading] = useState<boolean>(false);
	const [hasBookmarkCreationAccess, setBookmarkCreationAccess] = useState<boolean>(false);
	const [hasAdminhubAccess, setHasAdminhubAccess] = useState<boolean>(false);
	const key = `${orgId}:${cloudId}`;

	const resource = useMemo(() => {
		return {
			orgAdmin: `ari:cloud:platform::org/${orgId}`,
			confluenceAdmin: `ari:cloud:confluence::site/${cloudId}`,
			jiraAdmin: `ari:cloud:jira::site/${cloudId}`,
			siteAdmin: `ari:cloud:platform::site/${cloudId}`,
		};
	}, [orgId, cloudId]);

	useEffect(() => {
		if (!cloudId || !orgId) {
			return;
		}

		const callApi = async () => {
			let responseError: Error | undefined;
			setLoading(true);
			try {
				let data = InMemCache.getCacheData(key);
				if (!data) {
					const response = await fetch('/gateway/api/permissions/bulk/permitted', {
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
						},
						body: JSON.stringify([
							{
								permissionId: 'manage/org',
								resourceId: resource.orgAdmin,
							},
							{
								permissionId: 'manage',
								resourceId: resource.confluenceAdmin,
							},
							{
								permissionId: 'manage',
								resourceId: resource.jiraAdmin,
							},
							{
								permissionId: 'manage',
								resourceId: resource.siteAdmin,
							},
						]),
					});
					data = await response.json();
					if (!Array.isArray(data)) {
						responseError = new Error(data?.errorDetail || '/permissions/bulk/permitted failure');
						throw responseError;
					}

					InMemCache.setCacheData(key, data);
				}
				setLoading(false);

				return data;
			} catch (error) {
				logger.error({
					feature: 'bookmarks',
					element: 'container',
					operation: 'fetch',
					category: 'network',
					error: error === responseError ? undefined : error,
					legacyError: error,
					message: 'Bookmark permissions request failed',
					functionName: 'useAdminPermissions',
				});
				setLoading(false);
				return [];
			}
		};

		const checkPermissions = async () => {
			const permissions = await callApi();
			let userIsSomeAdmin = false;
			let userIsOrgAdmin = false;

			if (!permissions?.length) {
				return;
			}

			for (const permission of permissions) {
				// allow creation of bookmarks if the user is any type of admin
				if (permission.permitted) {
					userIsSomeAdmin = true;
				}
				if (permission.resourceId === resource.orgAdmin && permission.permitted) {
					userIsOrgAdmin = true;
				}
			}
			setBookmarkCreationAccess(userIsSomeAdmin);
			setHasAdminhubAccess(userIsOrgAdmin);
		};

		checkPermissions();
	}, [cloudId, orgId, key, resource]);

	return {
		hasBookmarkCreationAccess,
		hasAdminhubAccess,
		loading,
	};
};
